<template>
  <!-- Key on the guest/auth boundary by ROUTE, not auth state: keying on
       auth.isAuthenticated remounts the still-active login route the instant
       login() sets the token (before navigation), flashing an empty form.
       The authenticated key also carries the TENANT: an org switch remounts
       the whole tree, so every component-bound composable refetches under the
       new X-Organization-Id (useOrganizationSwitch lands the id write in the
       same flush as the route change, so home mounts once). -->
  <router-view :key="route.meta.isGuest ? 'guest' : `auth:${orgStore.currentOrganizationId}`" />
  <Toaster position="bottom-right" :rich-colors="true" :close-button="true" />
</template>

<script setup lang="ts">
import { watch, onMounted, onUnmounted, provide, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { Toaster } from 'vue-sonner'
import { useAuthStore } from '@/stores/auth'
import { useOrganizationStore } from '@/stores/organization'
import { useOrganizationChannel, ORG_CHANNEL_KEY } from '@/composables/useOrganizationChannel'
import { useImportNotifications } from '@/composables/useImportNotifications'
import { usePushNotifications } from '@/composables/usePushNotifications'

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const orgStore = useOrganizationStore()

// ── ActionCable — one singleton for the app lifetime ──────────────
const channel = useOrganizationChannel()
// Provide to all child composables that need real-time updates (e.g. useImport)
provide(ORG_CHANNEL_KEY, channel)

// Connect when auth + org are ready; disconnect on logout or org change.
// ⚠ auth.token is a WATCH SOURCE on purpose: the consumer bakes the JWT into
// its /cable URL, and access tokens live 30 minutes — without re-keying on
// refresh, the first socket drop after expiry reconnects with the dead token
// and the server rejects it FOREVER (log-proven: « An unauthorized connection
// attempt was rejected » on every retry while broadcasts fire into an empty
// room). Handlers survive the reconnect by design.
watch(
  [() => auth.isAuthenticated, () => orgStore.currentOrganization, () => auth.token],
  ([isAuth, currentOrg]) => {
    channel.disconnect()
    if (isAuth && currentOrg && auth.token) {
      channel.connect(auth.token, currentOrg.id)
    }
  },
  { immediate: true }
)

// Global import completion toasts
useImportNotifications(channel)

// ── Web push notifications ─────────────────────────────────────────
// init() attaches the foreground + SW-click listeners (no permission prompt);
// registration (which prompts) is deferred until the user is authenticated, so
// we never ask before login. immediate covers a refresh while already signed in.
const push = usePushNotifications()
onMounted(() => { void push.init() })
watch(() => auth.isAuthenticated, (isAuth) => {
  if (isAuth) void push.register()
}, { immediate: true })

// Full teardown on app unmount (HMR, logout-and-remount, etc.)
onUnmounted(() => channel.teardown())

// ── Global Logout Redirection + deferred profile teardown ──────────
// On de-auth (logout button OR silent 401-forced logout), redirect to /login,
// then clear the profile only AFTER the route has left the protected subtree.
// nextTick lets the keyed <router-view> patch (old subtree unmounts) before
// clearProfile() nulls `user`, so no mounted protected component — which may
// read auth.currentUser — ever observes a null user.
watch(() => auth.isAuthenticated, async (isAuth) => {
  if (!isAuth && !route.meta.isGuest) {
    await router.replace('/login')
    await nextTick()
    auth.clearProfile()
  }
})
</script>
